import { useContext, useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
    Button,
    Label,
    Offcanvas,
    OffcanvasBody,
    OffcanvasHeader,
} from 'reactstrap';
import Select from 'react-select';
import { renderIcon } from '~/shared/components/icons';
import DisbursementsTable from './DisbursementsTable';
import { TextInput } from '~/shared/components/form/Inputs';
import { downloadCSV } from '~/services/csv';
import { fetchMyCampaign, fetchMyDisbursements } from '~/services/campaign';
import {
    Disbursement,
    GroupedDisbursement,
    MyDisbursementsResponse,
} from '~/types/disbursements';
import { CampaignContext } from '~/context/campaign';
import { MyCampaignResponse } from '~/types/campaign';
import { CampaignHeaderSection } from '~/shared/components/cards/CampaignItemsCard';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
const to = moment(new Date()).format('YYYY-MM-DD');
const from = moment(to).subtract(30, 'days').format('YYYY-MM-DD');

type PayeeFilterOption = {
    value: string;
    label: string;
};

const Disbursements = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [filterLoading, setFilterLoading] = useState<boolean>(false);
    const [filterDrawer, setFilterDrawer] = useState<boolean>(false);
    const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
    const [selectedPayeeFilter, setSelectedPayeeFilter] =
        useState<PayeeFilterOption | null>(null);
    const { register, handleSubmit, control } = useForm({
        defaultValues: { to, from },
    });
    const [disbursementsResponse, setDisbursementsResponse] =
        useState<MyDisbursementsResponse>();
    const [groupedDisbursements, setGroupedDisbursements] = useState<
        GroupedDisbursement[]
    >([]);
    // const [totals, setTotals] = useState<DisbursementTotalResponse[]>();
    const history = useHistory();

    // Create filter options from grouped disbursements
    const payeeFilterOptions: PayeeFilterOption[] = useMemo(() => {
        const uniquePayees = Array.from(
            new Set(
                groupedDisbursements.map((disbursement) => disbursement.name)
            )
        )
            .sort()
            .map((name) => ({
                value: name,
                label: name,
            }));
        return uniquePayees;
    }, [groupedDisbursements]);

    // Filter disbursements based on selected filter
    const filteredDisbursements = useMemo(() => {
        if (!selectedPayeeFilter) {
            return groupedDisbursements;
        }
        return groupedDisbursements.filter(
            (disbursement) => disbursement.name === selectedPayeeFilter.value
        );
    }, [groupedDisbursements, selectedPayeeFilter]);

    const handlePayeeFilterChange = (
        selectedOption: PayeeFilterOption | null
    ) => {
        setSelectedPayeeFilter(selectedOption);
    };

    function DisbursementsText() {
        return (
            <div>
                <h1 className='h2'>Disbursements</h1>
                <p>
                    Check out all the fund requests Help Hope Live processed for
                    your campaign this month! Adjust the start and end dates to
                    view disbursements from a different time frame.
                </p>
            </div>
        );
    }

    const handleDisbursementsResponse = (resp: MyDisbursementsResponse) => {
        setDisbursementsResponse(resp);
        const groupedDisbursementsMap = Map.groupBy(
            resp.disbursements,
            (d) => d.vendor_id
        );
        const grouped = Object.entries(resp.totals).map(
            ([vendor_id, total]) => {
                return {
                    ...total,
                    disbursements: groupedDisbursementsMap.get(vendor_id) || [],
                };
            }
        );
        setGroupedDisbursements(grouped as GroupedDisbursement[]);
    };

    const onSubmit = async ({ from, to }: { from: string; to: string }) => {
        const campaignId = campaign?.campaignDetail?.campaign_id;
        setFilterLoading(true);
        try {
            const resp: MyDisbursementsResponse = await fetchMyDisbursements(
                from,
                to,
                campaignId
            );
            setDisbursementsResponse(resp);
            handleDisbursementsResponse(resp);
        } catch (e) {
            console.log('onSubmit() e: ', e);
            // Show some error here
        } finally {
            setFilterLoading(false);
        }
    };

    const getMyCampaign = async () => {
        try {
            const resp: MyCampaignResponse = await fetchMyCampaign();
            if (resp.detail) {
                if (resp.detail === 'Invalid token.') {
                    // Delete localStorage token
                    localStorage.removeItem('token');
                    history.push('/login');
                } else {
                    toast.error(resp.detail);
                }
            } else {
                campaignDispatch({
                    type: 'set_campaign',
                    payload: resp,
                });
                setLoading(false);
            }
        } catch (e) {
            console.log('getMyCampaign() e: ', e);
            // Show some error here
        }
    };

    useEffect(() => {
        if (!campaign) {
            getMyCampaign();
        } else {
            setLoading(false);
        }
    }, []);

    const getMyDisbursements = async (
        fromDateStr: string | null = null,
        toDateStr: string | null = null
    ) => {
        try {
            const campaignId = campaign?.campaignDetail?.campaign_id;
            const { disbursements, totals }: MyDisbursementsResponse =
                await fetchMyDisbursements(fromDateStr, toDateStr, campaignId);
            handleDisbursementsResponse({ disbursements, totals });
        } catch (e) {
            console.log('getMyDisbursements() e: ', e);
            // Show some error here
        }
    };

    useEffect(() => {
        if (campaign && !disbursementsResponse) {
            getMyDisbursements(from, to);
        }
    }, [campaign]);

    const formatGroupedDisbursements = (
        groupedDisbursements: GroupedDisbursement[]
    ) => {
        let data: any[] = [];
        groupedDisbursements.map((groupedDisbursement: any) => {
            groupedDisbursement.disbursements.map(
                (disbursement: Disbursement) => {
                    const row = {
                        'Check #': disbursement.doc_number,
                        Vendor: groupedDisbursement.name,
                        Date: moment(disbursement.effective_date).format('L'),
                        Amount: disbursement.amount,
                        'Payee Address': disbursement.check_address !== undefined ? disbursement.check_address : groupedDisbursement.address,
                    };
                    data.push(row);
                }
            );
        });
        return data;
    };

    return (
        <div className='container__inner'>
            <CampaignHeaderSection leftcard={<DisbursementsText />} />
            <div className='filter-bar justify-content-xl-between align-items-center flex-wrap'>
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className='d-none d-lg-flex gap-3 align-items-center flex-shrink-0'
                >
                    <Label
                        className='fw-light'
                        style={{ textTransform: 'none' }}
                    >
                        Show expenses paid
                    </Label>
                    <div className='d-flex'>
                        <div>
                            <TextInput
                                name='from'
                                label=''
                                type='date'
                                control={control}
                                required={false}
                            />
                        </div>
                        <Label
                            className='fw-light'
                            style={{
                                padding: 15,
                                alignContent: 'center',
                                textTransform: 'none',
                            }}
                        >
                            to
                        </Label>
                        <div>
                            <TextInput
                                name='to'
                                label=''
                                type='date'
                                control={control}
                                required={false}
                            />
                        </div>
                    </div>
                    <div>
                        <Button 
                            color='outline-dark' 
                            size='sm' 
                            type='submit'
                            disabled={filterLoading}
                        >
                            {filterLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Loading...
                                </>
                            ) : (
                                'Filter'
                            )}
                        </Button>
                    </div>
                    {/* Payees Filter */}
                    <div style={{ minWidth: '200px' }}>
                        <Select
                            value={selectedPayeeFilter}
                            onChange={handlePayeeFilterChange}
                            options={payeeFilterOptions}
                            isClearable
                            placeholder='Filter by payee...'
                            className='react-select-container'
                            classNamePrefix='react-select'
                        />
                    </div>
                </form>
                <div className='d-none d-lg-block'>
                    <Button
                        color='outline-dark'
                        size='sm'
                        onClick={() =>
                            downloadCSV(
                                formatGroupedDisbursements(
                                    filteredDisbursements
                                ),
                                `Disbursements`
                            )
                        }
                    >
                        {renderIcon('download')} Export List
                    </Button>
                </div>

                <Button
                    color='outline-dark'
                    className='d-block d-lg-none w-100 m-0'
                    onClick={() => setFilterDrawer(true)}
                >
                    Filters
                </Button>
                <Offcanvas
                    direction='end'
                    fade={false}
                    isOpen={filterDrawer}
                    toggle={() => {
                        setFilterDrawer(false);
                    }}
                >
                    <OffcanvasHeader
                        className='text-uppercase'
                        toggle={() => {
                            setFilterDrawer(false);
                        }}
                    >
                        Filters
                    </OffcanvasHeader>
                    <OffcanvasBody>
                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            className='d-flex flex-column gap-3 justify-content-between h-100 pb-4'
                        >
                            <div className='d-flex flex-column'>
                                <Label
                                    className='fw-light'
                                    style={{ textTransform: 'none' }}
                                >
                                    Show expenses paid
                                </Label>
                                <div>
                                    <TextInput
                                        name='from'
                                        label=''
                                        type='date'
                                        control={control}
                                        required={false}
                                    />
                                </div>
                                <Label
                                    className='mb-3 fw-light'
                                    style={{
                                        textTransform: 'none',
                                    }}
                                >
                                    to
                                </Label>
                                <div className='mb-3'>
                                    <TextInput
                                        name='to'
                                        label=''
                                        type='date'
                                        control={control}
                                        required={false}
                                    />
                                </div>

                                {/* Mobile Payees Filter */}
                                <div className='mb-3'>
                                    <Label
                                        className='fw-light mb-2'
                                        style={{ textTransform: 'none' }}
                                    >
                                        Filter by payee
                                    </Label>
                                    <Select
                                        value={selectedPayeeFilter}
                                        onChange={handlePayeeFilterChange}
                                        options={payeeFilterOptions}
                                        isClearable
                                        placeholder='Select a payee...'
                                        className='react-select-container'
                                        classNamePrefix='react-select'
                                    />
                                </div>

                                <Button
                                    color='outline-dark'
                                    size='sm'
                                    onClick={() =>
                                        downloadCSV(
                                            formatGroupedDisbursements(
                                                filteredDisbursements
                                            ),
                                            `Disbursements`
                                        )
                                    }
                                >
                                    {renderIcon('download')} Export List
                                </Button>
                            </div>
                            <div className='sticky-bottom'>
                                <Button
                                    color='outline-dark'
                                    size='sm'
                                    type='submit'
                                    className='d-block w-100'
                                    disabled={filterLoading}
                                >
                                    {filterLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Loading...
                                        </>
                                    ) : (
                                        'Filter'
                                    )}
                                </Button>
                            </div>
                        </form>
                    </OffcanvasBody>
                </Offcanvas>
            </div>
            <div
                className='pb-5'
                style={{ overflow: 'visible', paddingBottom: '120px' }}
            >
                <DisbursementsTable
                    groupedDisbursements={filteredDisbursements}
                />
            </div>
        </div>
    );
};

export default Disbursements;
